import React, { useState, useEffect, useCallback, useRef } from 'react';

import usePurchaseOrder from '../hooks/usePurchaseOrder';
import PurchaseOrdersActions from '../components/purchase-orders/PurchaseOrdersActions';
import PurchaseOrdersTable from '../components/purchase-orders/PurchaseOrdersTable';
import PurchaseOrdersViewModal from '../components/purchase-orders/PurchaseOrdersViewModal';
import PurchaseOrdersForm from '../components/purchase-orders/PurchaseOrdersForm';
import PurchaseOrderAdviceForm from '../components/purchase-orders/PurchaseOrderAdviceForm';
import PurchaseChallanPrintModal from '../components/purchase-orders/PurchaseChallanPrintModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SuperAdminDeleteDialog from '../components/common/SuperAdminDeleteDialog';
import StockRevertChoice from '../components/common/StockRevertChoice';
import MessageAlert from '../components/common/MessageAlert';
import Pagination from '../components/common/Pagination';
import { useErrorDialog } from '../context/errorDialogContext';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useRole } from '../context/roleContext';
import { getDefaultDateRange } from '../utils/format';
import purchaseOrderService from '../services/purchaseOrderService';
import { useTranslation } from 'react-i18next';


const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'purchase_orders_page_size';

/**
 * Has this purchase order actually put anything into the warehouse yet? Only then is
 * "revert the stock?" a real question worth putting to the operator.
 *
 * `has_moved_stock` on POListResponse is the authority and the only signal read here.
 * Stock arrives on a PO by two routes the status alone cannot separate — a receipt
 * (whole PO or a single advice) and coils entered at unloading, which happens before any
 * challan exists — so reconstructing it from `status` client-side would get it wrong.
 */
const purchaseOrderMovedStock = (order) => order?.has_moved_stock === true;

const PurchaseOrdersPage = () => {
    const { t } = useTranslation();
    const {
        purchaseOrders,
        loading,
        error,
        totalCount,
        fetchPurchaseOrders,
        createPurchaseOrder,
        updatePurchaseOrder,
        deletePurchaseOrder,
        confirmPurchaseOrder,
        cancelPurchaseOrder,
        generatePurchaseAdvice,
        markAllAdvised,
        getPurchaseOrderById,
        fetchProductSizes,
        totalPages,
        deleteAllPurchaseOrders,
        bulkDeletePurchaseOrders,
    } = usePurchaseOrder();

    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });

    const [selectedOrders, setSelectedOrders] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [viewingOrder, setViewingOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('pending');
    const [currentPage, setCurrentPage] = useState(1);
    const searchDebounceRef = useRef(null);

    const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
    const { from_date: fromDate, to_date: toDate } = dateRange;

    const handleFromDateChange = (val) => {
        setDateRange(prev => ({ ...prev, from_date: val }));
        setCurrentPage(1);
    };

    const handleToDateChange = (val) => {
        setDateRange(prev => ({ ...prev, to_date: val }));
        setCurrentPage(1);
    };

    // Generic confirmation dialog state
    const [actionDialog, setActionDialog] = useState(null);
    // Answer to the delete dialog's stock question. Reset to "give it back" every time
    // the dialog opens, so a previous "leave it" cannot carry into the next delete.
    const [revertStock, setRevertStock] = useState(true);
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editingOrderId, setEditingOrderId] = useState(null);

    // Advice Form state
    const [showAdviceForm, setShowAdviceForm] = useState(false);
    const [adviceOrder, setAdviceOrder] = useState(null);


    // Print Challan Modal state
    const [showPrintChallanModal, setShowPrintChallanModal] = useState(false);
    const [printChallanData, setPrintChallanData] = useState(null);
    const [printLoading, setPrintLoading] = useState(false);

    const strings = t('purchaseOrders', { returnObjects: true });
    const { showError } = useErrorDialog();
    const { selectedWarehouseIds } = useWarehouseContext();
    const { checkDeleteAccess } = useRole();


    // Debounce search → reset to page 1
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setCommittedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchTerm]);

    // Reset page when status filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    // Fetch on page / search / status change
    useEffect(() => {
        const skip = (currentPage - 1) * pageSize;
        void fetchPurchaseOrders({
            skip,
            limit: pageSize,
            ...(committedSearch && { search: committedSearch }),
            status: statusFilter ? [statusFilter] : undefined,
            from_date: fromDate,
            to_date: toDate,
        });
    }, [fetchPurchaseOrders, currentPage, committedSearch, statusFilter, selectedWarehouseIds, pageSize, fromDate, toDate]);

    useEffect(() => {
        void fetchProductSizes({ limit: 20 });
    }, [fetchProductSizes]);

    useEffect(() => {
        if (error) showError(error, t('common.alerts.errorTitle'));
    }, [error, showError]);

    const filteredOrders = purchaseOrders;

    const selectOrder = (id, checked) => {
        setSelectedOrders(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
    };

    const selectAllOrders = (checked) => {
        const currentPageIds = filteredOrders.map(i => i.id);
        if (checked) {
            setSelectedOrders(prev => {
                const set = new Set([...prev, ...currentPageIds]);
                return Array.from(set);
            });
        } else {
            setSelectedOrders(prev => prev.filter(id => !currentPageIds.includes(id)));
        }
    };

    const hasSelection = selectedOrders.length > 0;
    const selectionCount = selectedOrders.length;

    // One question for the whole selection: ask if any selected order would have been
    // asked about on its own. Only loaded rows can be inspected, so a selection carried
    // over from a page that has since been replaced is judged on what is on screen.
    const selectionMovedStock = filteredOrders.some(
        (o) => selectedOrders.includes(o.id || o.po_id) && purchaseOrderMovedStock(o),
    );

    const handleRefresh = async () => {
        setRefreshLoading(true);
        try {
            const skip = (currentPage - 1) * pageSize;
            await fetchPurchaseOrders({
                skip,
                limit: pageSize,
                ...(committedSearch && { search: committedSearch }),
                status: statusFilter ? [statusFilter] : undefined,
                from_date: fromDate,
                to_date: toDate,
            }, { force: true });
        } catch (err) {
            console.error('Failed to refresh purchase orders', err);
        } finally {
            setRefreshLoading(false);
        }
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    const handleAddOrder = () => {
        setEditingOrderId(null);
        setShowForm(true);
    };

    const handleEditOrder = (orderId) => {
        setEditingOrderId(orderId);
        setShowForm(true);
    };

    const handleFormSubmit = async (payload) => {
        try {
            let result;
            if (editingOrderId) {
                result = await updatePurchaseOrder(editingOrderId, payload) || {};
            } else {
                result = await createPurchaseOrder(payload) || {};
            }

            if (result?.success) {
                setShowForm(false);
                setEditingOrderId(null);
            } else {
                showError(result?.error || t('common.alerts.errorMessage'), t('common.alerts.errorTitle'));
            }
            // Return the result so the form can decide whether to close (it only
            // closes on success). Without this the edit path saw `undefined`.
            return result;
        } catch (err) {
            console.error('Form submit failed', err);
            showError(err.message, t('common.alerts.errorTitle'));
            return { success: false, error: err.message };
        }
    };

    const handleViewOrder = async (orderId) => {
        setViewLoading(true);
        setViewingOrder(null);
        setShowViewModal(true);
        try {
            const result = await getPurchaseOrderById(orderId, { force: true }) || {};
            if (result?.success && result?.data) setViewingOrder(result.data);
            else showError(result?.error || t('common.alerts.fetchFailed'), t('common.alerts.errorTitle'));
        } catch (err) {
            console.error('View purchase order failed', err);
            showError(err.message || t('common.alerts.fetchFailed'), t('common.alerts.errorTitle'));
        } finally {
            setViewLoading(false);
        }
    };

    const handleDeleteOrder = (orderId) => {
        const order = (filteredOrders || []).find(i => (i.id || i.po_id) === orderId) || null;
        const displayName = order?.po_number || orderId;
        const movedStock = purchaseOrderMovedStock(order);
        // Deleting a PO past `pending` also throws away its advices and releases the
        // pending-purchase stock it had reserved, so say so before it happens. Once the
        // goods are actually in, what becomes of the stock is the operator's call, and
        // the choice in the dialog spells out both outcomes instead.
        const isMidFlow = !['pending', 'cancelled'].includes((order?.status || '').toLowerCase());
        setRevertStock(true);
        setActionDialog({
            type: 'delete',
            orderId,
            asksStockChoice: movedStock,
            title: strings.table?.tooltips?.delete || 'Delete Purchase Order',
            message: isMidFlow && !movedStock
                ? `Are you sure you want to delete Purchase Order ${displayName}? Its advices will be removed and the pending purchase quantity it reserved will be released. This cannot be undone.`
                : `Are you sure you want to delete Purchase Order ${displayName}?`,
            confirmLabel: t('common.dialogs.delete.confirm'),
            cancelLabel: t('common.dialogs.delete.cancel'),
            confirmVariant: 'danger',
        });
    };

    const handleBulkDelete = () => {
        if (!checkDeleteAccess() || selectedOrders.length === 0) return;
        setShowBulkDeleteModal(true);
    };

    const handleConfirmBulkDelete = async (password, { revertStock: bulkRevertStock = false } = {}) => {
        setActionLoading(true);
        try {
            // selectedOrders are PURCHASE ORDER ids (rows of GET /purchase-orders).
            const result = await bulkDeletePurchaseOrders(selectedOrders || [], password, { revertStock: bulkRevertStock, idKind: 'po' });
            if (result.success) {
                setShowBulkDeleteModal(false);
                setSelectedOrders([]);
            } else {
                setAlertMessage({ type: 'error', title: t('common.status.failed'), message: result?.error || 'Failed to bulk delete purchase orders' });
            }
        } catch (err) {
            console.error('Bulk delete failed', err);
            setAlertMessage({ type: 'error', title: t('common.status.failed'), message: err.message || 'Failed to bulk delete purchase orders' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmOrder = (orderId) => {
        const order = (filteredOrders || []).find(i => (i.id || i.po_id) === orderId) || null;
        const displayName = order?.po_number || orderId;
        setActionDialog({
            type: 'confirm',
            orderId,
            title: strings.confirm?.confirm?.title || 'Confirm Purchase Order',
            message: strings.confirm?.confirm?.message?.replace('{poNumber}', displayName) || `Are you sure you want to confirm Purchase Order ${displayName}?`,
            confirmLabel: strings.confirm?.confirm?.confirmLabel || 'Yes, Confirm',
            cancelLabel: strings.confirm?.confirm?.cancelLabel || 'Cancel',
            confirmVariant: 'success',
        });
    };

    const handleCancelOrder = (orderId) => {
        const order = (filteredOrders || []).find(i => (i.id || i.po_id) === orderId) || null;
        const displayName = order?.po_number || orderId;
        setActionDialog({
            type: 'cancel',
            orderId,
            title: strings.confirm?.cancel?.title || 'Cancel Purchase Order',
            message: strings.confirm?.cancel?.message?.replace('{poNumber}', displayName) || `Are you sure you want to cancel Purchase Order ${displayName}?`,
            confirmLabel: strings.confirm?.cancel?.confirmLabel || 'Yes, Cancel',
            cancelLabel: strings.confirm?.cancel?.cancelLabel || 'No, Keep',
            confirmVariant: 'danger',
        });
    };

    const handleGenerateAdvice = (orderId) => {
        console.log('handleGenerateAdvice called with ID:', orderId, 'Type:', typeof orderId);
        const order = (filteredOrders || []).find(i => (i.id || i.po_id || i.po_number) === orderId || String(i.id) === String(orderId) || String(i.po_id) === String(orderId)) || null;
        console.log('Found order for advice:', order);
        if (order) {
            setAdviceOrder(order);
            setShowAdviceForm(true);
        } else {
            console.error('No order found in filteredOrders matching ID:', orderId);
            console.log('Available orders sample:', (filteredOrders || []).slice(0, 3).map(i => ({ id: i.id, po_id: i.po_id, po_number: i.po_number })));
        }
    };

    const handleAdviceSubmit = async (payload) => {
        try {
            if (!adviceOrder) return { success: false };
            const result = await generatePurchaseAdvice(adviceOrder.id || adviceOrder.po_id, payload) || {};
            if (result?.success) {
                setShowAdviceForm(false);
                setAdviceOrder(null);
                void handleRefresh();
            } else {
                showError(result?.error || t('common.alerts.errorMessage'), t('common.alerts.errorTitle'));
            }
            // Return the result so the form keeps itself open (and its data) on failure.
            return result;
        } catch (err) {
            console.error('Advice generation failed', err);
            showError(err.message, t('common.alerts.errorTitle'));
            return { success: false, error: err.message };
        }
    };



    const handleGoToAdvice = useCallback((order) => {
        // Navigate to purchase advice page
        const poId = order?.id || order?.po_id;
        if (poId) {
            window.dispatchEvent(new CustomEvent('navigate', { detail: { page: 'purchase-advice', po_id: poId } }));
        }
    }, []);

    const handleMarkAllAdvised = useCallback((orderId) => {
        const order = (filteredOrders || []).find(i => (i.id || i.po_id) === orderId) || null;
        const displayName = order?.po_number || orderId;
        const status = (order?.status || '').toLowerCase();

        const isMarkAllAdvised = ['confirmed', 'partial_advice_created'].includes(status);

        setActionDialog({
            type: 'markAllAdvised',
            orderId,
            title: isMarkAllAdvised ? 'Mark All Items as Advised' : 'Mark Purchase Order as Complete',
            message: isMarkAllAdvised 
                ? `Are you sure you want to mark all items in Purchase Order ${displayName} as advised? This will skip individual advice creation and move the order forward.`
                : `Are you sure you want to mark Purchase Order ${displayName} as complete? This will finalize all advices.`,
            confirmLabel: isMarkAllAdvised ? 'Yes, Mark All Advised' : 'Yes, Mark Complete',
            cancelLabel: t('common.buttons.cancel'),
            confirmVariant: 'success',
        });
    }, [filteredOrders]);

    const handlePrintChallan = async (orderId) => {
        try {
            setPrintChallanData(null);
            setShowPrintChallanModal(true);
            setPrintLoading(true);
            const result = await getPurchaseOrderById(orderId, { force: true }) || {};
            if (result?.success) {
                setPrintChallanData(result?.data);
            } else {
                setShowPrintChallanModal(false);
                showError(result?.error || 'Failed to load order details for printing', t('common.alerts.errorTitle'));
            }
        } catch (err) {
            console.error('Failed to load order for printing', err);
            setShowPrintChallanModal(false);
            showError(t('common.alerts.errorMessage'), t('common.alerts.errorTitle'));
        } finally {
            setPrintLoading(false);
        }
    };

    const handleDialogConfirm = async () => {
        if (!actionDialog) return;
        setActionLoading(true);
        try {
            const { type, orderId } = actionDialog;
            let result;

            if (type === 'delete') {
                result = await deletePurchaseOrder(orderId, { revertStock }) || {};
                if (result.success) setSelectedOrders(prev => prev.filter(oid => oid !== orderId));
            } else if (type === 'confirm') {
                result = await confirmPurchaseOrder(orderId) || {};
            } else if (type === 'cancel') {
                result = await cancelPurchaseOrder(orderId) || {};
            } else if (type === 'markAllAdvised') {
                result = await markAllAdvised(orderId) || {};
                if (result.success) {
                    void handleRefresh();
                }
            }

            if (result && result.success) {
                setActionDialog(null);
            } else {
                setAlertMessage({ type: 'error', title: t('common.status.error'), message: result?.error || 'Action failed' });
            }
        } catch (err) {
            console.error('Action failed', err);
            setAlertMessage({ type: 'error', title: t('common.status.error'), message: err.message || 'Action failed' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmDeleteAll = async (password) => {
        setActionLoading(true);
        try {
            const result = await deleteAllPurchaseOrders(password);
            if (result.success) {
                setShowDeleteAllModal(false);
                setSelectedOrders([]);
            } else {
                setAlertMessage({ type: 'error', title: t('common.status.failed'), message: result?.error || 'Failed to delete all purchase orders' });
            }
        } catch (err) {
            console.error('Delete all failed', err);
            setAlertMessage({ type: 'error', title: t('common.status.failed'), message: err.message || 'Failed to delete all purchase orders' });
        } finally {
            setActionLoading(false);
        }
    };

    const countOrders = () => {
        if (typeof totalCount === 'number' && totalCount >= 0) return totalCount;
        if (Array.isArray(purchaseOrders)) return purchaseOrders.length;
        return 0;
    };

    const totalOrdersCount = countOrders();

    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
            {alertMessage && (
                <div className="fixed top-4 right-4 z-50">
                    <MessageAlert type={alertMessage.type} title={alertMessage.title} message={alertMessage.message} onClose={() => setAlertMessage(null)} />
                </div>
            )}

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('navigation.pages.purchaseOrders')}</h1>
                        <p className="mt-2 text-gray-600">{t('navigation.pageSubtitles.purchaseOrders')}</p>
                    </div>

                    <div className="flex space-x-4">
                        <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-3">
                                <div className="text-sm text-gray-500">{t('navigation.pageMetrics.totalOrders')}</div>
                                <div className="text-2xl font-bold text-gray-900">{totalOrdersCount}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PurchaseOrdersActions
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                onAddOrder={handleAddOrder}
                onRefresh={handleRefresh}
                onDeleteAll={() => setShowDeleteAllModal(true)}
                onBulkDelete={handleBulkDelete}
                hasSelection={hasSelection}
                selectionCount={selectionCount}
                loading={actionLoading}
                refreshLoading={refreshLoading}
                statusValue={statusFilter}
                onStatusChange={setStatusFilter}
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
            />

            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
                <PurchaseOrdersTable
                    orders={filteredOrders}
                    loading={loading}
                    selectedOrders={selectedOrders}
                    onSelectOrder={selectOrder}
                    onSelectAll={selectAllOrders}
                    onEditOrder={handleEditOrder}
                    onDeleteOrder={handleDeleteOrder}
                    onViewOrder={handleViewOrder}
                    onConfirmOrder={handleConfirmOrder}
                    onCancelOrder={handleCancelOrder}
                    onGenerateAdvice={handleGenerateAdvice}
                    onMarkAllAdvised={handleMarkAllAdvised}
                    onPrintChallan={handlePrintChallan}
                    onGoToAdvice={handleGoToAdvice}
                    viewLoading={viewLoading}
                    onSendNotification={(id, type) => purchaseOrderService.sendNotification(id, type)}
                />
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalOrdersCount}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total orders"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />

            {showForm && (
                <PurchaseOrdersForm
                    isOpen={showForm}
                    onClose={() => { setShowForm(false); setEditingOrderId(null); }}
                    onSubmit={handleFormSubmit}
                    onSuccess={async () => {
                        setShowForm(false);
                        setEditingOrderId(null);
                        try { await fetchPurchaseOrders({}, { force: true }); } catch (e) { console.error('Failed to refresh after create', e); }
                    }}
                    orderId={editingOrderId}
                />
            )}

            {showViewModal && (
                <PurchaseOrdersViewModal
                    isOpen={showViewModal}
                    onClose={() => { setShowViewModal(false); setViewingOrder(null); }}
                    order={viewingOrder}
                    loading={viewLoading}
                />
            )}

            {actionDialog && (
                <ConfirmDialog
                    isOpen
                    title={actionDialog.title}
                    message={actionDialog.asksStockChoice ? (
                        <div className="w-full">
                            <p className="mb-3">{actionDialog.message}</p>
                            <StockRevertChoice
                                variant="single"
                                value={revertStock}
                                onChange={setRevertStock}
                                disabled={actionLoading}
                                name="po-delete-revert-stock"
                            />
                        </div>
                    ) : actionDialog.message}
                    confirmLabel={actionDialog.confirmLabel}
                    cancelLabel={actionDialog.cancelLabel}
                    confirmVariant={actionDialog.confirmVariant}
                    loading={actionLoading}
                    onConfirm={handleDialogConfirm}
                    onCancel={() => setActionDialog(null)}
                />
            )}

            {showAdviceForm && (
                <PurchaseOrderAdviceForm
                    isOpen={showAdviceForm}
                    onClose={() => { setShowAdviceForm(false); setAdviceOrder(null); }}
                    onSubmit={handleAdviceSubmit}
                    orderId={adviceOrder?.id || adviceOrder?.po_id}
                />
            )}

            {showPrintChallanModal && (
                <PurchaseChallanPrintModal
                    isOpen={showPrintChallanModal}
                    onClose={() => setShowPrintChallanModal(false)}
                    data={printChallanData}
                    loading={printLoading}
                />
            )}

            {showDeleteAllModal && (
                <SuperAdminDeleteDialog
                    isOpen={showDeleteAllModal}
                    typeLabel="All Purchase Orders"
                    loading={actionLoading}
                    onConfirm={handleConfirmDeleteAll}
                    onCancel={() => setShowDeleteAllModal(false)}
                />
            )}

            {showBulkDeleteModal && (
                <SuperAdminDeleteDialog
                    isOpen={showBulkDeleteModal}
                    typeLabel={`${selectedOrders.length} Selected Purchase Orders`}
                    askStockRevert={selectionMovedStock}
                    loading={actionLoading}
                    onConfirm={handleConfirmBulkDelete}
                    onCancel={() => setShowBulkDeleteModal(false)}
                />
            )}
        </div>
    );
};

export default PurchaseOrdersPage;
